import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API, useAuth } from '@/App';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Bell, Plus, Trash2, Mail, Clock, MapPin, 
  BookOpen, GraduationCap, Eye, Edit2
} from 'lucide-react';

const JobAlertsPage = () => {
  const { user, token } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [constants, setConstants] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [matchingJobs, setMatchingJobs] = useState({});
  
  const [formData, setFormData] = useState({
    frequency: 'daily',
    job_category: '',
    subjects: [],
    grade_levels: [],
    locations: [],
    job_type: ''
  });
  const [locationInput, setLocationInput] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [alertsRes, constantsRes] = await Promise.all([
          axios.get(`${API}/job-alerts`, { headers, withCredentials: true }),
          axios.get(`${API}/education/constants`)
        ]);
        setAlerts(alertsRes.data.alerts || []);
        setConstants(constantsRes.data);
      } catch (error) {
        console.error('Failed to fetch alerts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      }
      return { ...prev, [field]: [...current, value] };
    });
  };

  const addLocation = () => {
    if (locationInput.trim() && !formData.locations.includes(locationInput.trim())) {
      setFormData(prev => ({
        ...prev,
        locations: [...prev.locations, locationInput.trim()]
      }));
      setLocationInput('');
    }
  };

  const removeLocation = (loc) => {
    setFormData(prev => ({
      ...prev,
      locations: prev.locations.filter(l => l !== loc)
    }));
  };

  const handleCreateAlert = async () => {
    setCreating(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/job-alerts`, formData, { 
        headers, 
        withCredentials: true 
      });
      setAlerts(prev => [...prev, response.data]);
      setShowCreateDialog(false);
      setFormData({
        frequency: 'daily',
        job_category: '',
        subjects: [],
        grade_levels: [],
        locations: [],
        job_type: ''
      });
      toast.success('Job alert created! You\'ll receive matching jobs via email.');
    } catch (error) {
      toast.error('Failed to create alert');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/job-alerts/${alertId}`, { headers, withCredentials: true });
      setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
      toast.success('Alert deleted');
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  const handleToggleAlert = async (alertId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/job-alerts/${alertId}/toggle`, {}, { 
        headers, 
        withCredentials: true 
      });
      setAlerts(prev => prev.map(a => 
        a.alert_id === alertId ? { ...a, is_active: response.data.is_active } : a
      ));
    } catch (error) {
      toast.error('Failed to toggle alert');
    }
  };

  const fetchMatchingJobs = async (alertId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/job-alerts/matching-jobs/${alertId}`, { 
        headers, 
        withCredentials: true 
      });
      setMatchingJobs(prev => ({ ...prev, [alertId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch matching jobs:', error);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn" data-testid="job-alerts-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Job Alerts
            </h1>
            <p className="text-slate-600 mt-1">
              Get notified when jobs matching your preferences are posted
            </p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-alert-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Alert
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit' }}>Create Job Alert</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 mt-4">
                {/* Frequency */}
                <div>
                  <Label>Alert Frequency</Label>
                  <Select value={formData.frequency} onValueChange={(v) => handleChange('frequency', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily Digest</SelectItem>
                      <SelectItem value="weekly">Weekly Summary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Category */}
                <div>
                  <Label>Job Category</Label>
                  <Select value={formData.job_category} onValueChange={(v) => handleChange('job_category', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Any category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Any Category</SelectItem>
                      <SelectItem value="teaching">Teaching Positions</SelectItem>
                      <SelectItem value="non_teaching">Non-Teaching Positions</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Subjects */}
                <div>
                  <Label>Subjects (Optional)</Label>
                  <div className="flex flex-wrap gap-2 mt-2 p-3 border rounded-lg bg-slate-50 max-h-32 overflow-y-auto">
                    {constants?.subjects?.slice(0, 20).map(subject => (
                      <Badge
                        key={subject}
                        variant={formData.subjects.includes(subject) ? "default" : "outline"}
                        className="cursor-pointer text-xs"
                        onClick={() => handleMultiSelect('subjects', subject)}
                      >
                        {subject}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Grade Levels */}
                <div>
                  <Label>Grade Levels (Optional)</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {constants?.grade_levels?.map(level => (
                      <Badge
                        key={level}
                        variant={formData.grade_levels.includes(level) ? "default" : "outline"}
                        className="cursor-pointer text-xs"
                        onClick={() => handleMultiSelect('grade_levels', level)}
                      >
                        {level}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Locations */}
                <div>
                  <Label>Locations</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={locationInput}
                      onChange={(e) => setLocationInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLocation())}
                      placeholder="Add city (e.g. Mumbai)"
                    />
                    <Button variant="outline" onClick={addLocation}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {formData.locations.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.locations.map(loc => (
                        <Badge key={loc} variant="secondary" className="pl-2 pr-1">
                          {loc}
                          <button onClick={() => removeLocation(loc)} className="ml-1 hover:bg-slate-300 rounded-full p-0.5">
                            <span className="text-xs">×</span>
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Job Type */}
                <div>
                  <Label>Job Type</Label>
                  <Select value={formData.job_type} onValueChange={(v) => handleChange('job_type', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Any type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Any Type</SelectItem>
                      <SelectItem value="full_time">Full Time</SelectItem>
                      <SelectItem value="part_time">Part Time</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreateAlert} disabled={creating}>
                    {creating ? 'Creating...' : 'Create Alert'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Alerts List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : alerts.length > 0 ? (
          <div className="space-y-4">
            {alerts.map(alert => (
              <Card key={alert.alert_id} className={`${!alert.is_active ? 'opacity-60' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <Bell className={`w-5 h-5 ${alert.is_active ? 'text-indigo-600' : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant={alert.frequency === 'daily' ? 'default' : 'secondary'}>
                              <Mail className="w-3 h-3 mr-1" />
                              {alert.frequency === 'daily' ? 'Daily' : 'Weekly'}
                            </Badge>
                            {!alert.is_active && <Badge variant="outline">Paused</Badge>}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-3">
                        {alert.job_category && (
                          <Badge variant="outline" className="bg-purple-50">
                            <GraduationCap className="w-3 h-3 mr-1" />
                            {alert.job_category === 'teaching' ? 'Teaching' : 'Non-Teaching'}
                          </Badge>
                        )}
                        {alert.subjects?.slice(0, 3).map(sub => (
                          <Badge key={sub} variant="outline" className="bg-blue-50 text-xs">
                            <BookOpen className="w-3 h-3 mr-1" />
                            {sub}
                          </Badge>
                        ))}
                        {alert.subjects?.length > 3 && (
                          <Badge variant="outline" className="text-xs">+{alert.subjects.length - 3} more</Badge>
                        )}
                        {alert.locations?.map(loc => (
                          <Badge key={loc} variant="outline" className="bg-green-50 text-xs">
                            <MapPin className="w-3 h-3 mr-1" />
                            {loc}
                          </Badge>
                        ))}
                        {alert.grade_levels?.slice(0, 2).map(grade => (
                          <Badge key={grade} variant="outline" className="text-xs">{grade}</Badge>
                        ))}
                      </div>

                      {/* Matching Jobs Preview */}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => fetchMatchingJobs(alert.alert_id)}
                        className="text-indigo-600"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Preview matching jobs
                      </Button>
                      
                      {matchingJobs[alert.alert_id] && (
                        <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm font-medium text-slate-700 mb-2">
                            {matchingJobs[alert.alert_id].total_matching} matching jobs found
                          </p>
                          <div className="space-y-2">
                            {matchingJobs[alert.alert_id].jobs?.slice(0, 3).map(job => (
                              <Link key={job.job_id} to={`/jobs/${job.job_id}`} className="block">
                                <div className="text-sm text-indigo-600 hover:underline">{job.title}</div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Switch
                        checked={alert.is_active}
                        onCheckedChange={() => handleToggleAlert(alert.alert_id)}
                      />
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteAlert(alert.alert_id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No job alerts yet</h3>
              <p className="text-slate-500 mb-4">
                Create an alert to get notified when new jobs match your preferences
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Alert
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="bg-indigo-50 border-indigo-100">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">How Job Alerts Work</h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Set your preferences for subjects, grades, and locations</li>
                  <li>• Choose daily or weekly email notifications</li>
                  <li>• We'll send you matching jobs as soon as they're posted</li>
                  <li>• Pause or delete alerts anytime</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default JobAlertsPage;
